import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { campaignApi } from '../api/campaigns.api';
import { useToast } from '../contexts/ToastContext';
import { Campaign, CampaignStatus } from '../types/campaign.types';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Button from '../components/common/Button';
import Modal from '../components/common/Modal';
import CampaignReceipt from '../components/campaigns/CampaignReceipt';
import AddContactForm from '../components/campaigns/AddContactForm';
import { formatDate, formatNumber, formatPercentage } from '../utils/formatters';
import { validateFileUpload } from '../utils/validators';

const CampaignDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [addContactModalOpen, setAddContactModalOpen] = useState(false);

  const fetchCampaignDetails = useCallback(async (campaignId: number) => {
    try {
      setLoading(true);
      const data = await campaignApi.get(campaignId, true);
      setCampaign(data);
    } catch (err) {
      showToast('Failed to load campaign details', 'error');
      navigate('/campaigns');
    } finally {
      setLoading(false);
    }
  }, [showToast, navigate]);

  useEffect(() => {
    if (id) {
      fetchCampaignDetails(parseInt(id));
    }
  }, [id, fetchCampaignDetails]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validation = validateFileUpload(file);
    if (!validation.valid) {
      showToast(validation.error!, 'error');
      return;
    }

    setSelectedFile(file);
  };

  const handleFileUpload = async () => {
    if (!selectedFile || !campaign) return;

    try {
      setUploading(true);
      const result = await campaignApi.uploadContacts(campaign.id, selectedFile);

      showToast(
        `Successfully uploaded ${result.valid_rows} contacts (${result.invalid_rows} invalid)`,
        'success'
      );

      setUploadModalOpen(false);
      setSelectedFile(null);

      // Refresh campaign details
      await fetchCampaignDetails(campaign.id);
    } catch (err) {
      showToast('Failed to upload contacts', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleExecuteCampaign = async () => {
    if (!campaign) return;

    if (campaign.total_pending === 0) {
      showToast('No pending contacts are ready to send', 'warning');
      return;
    }

    try {
      setExecuting(true);
      const result = await campaignApi.execute(campaign.id);
      showToast(result.message, 'success');

      // Refresh campaign details
      await fetchCampaignDetails(campaign.id);
    } catch (err) {
      showToast('Failed to execute campaign', 'error');
    } finally {
      setExecuting(false);
    }
  };

  const handleRetryFailed = async () => {
    if (!campaign) return;

    try {
      await campaignApi.retryFailed(campaign.id);
      showToast('Failed messages queued for retry', 'success');

      // Refresh after a delay
      setTimeout(() => fetchCampaignDetails(campaign.id), 2000);
    } catch (err) {
      showToast('Failed to retry messages', 'error');
    }
  };

  if (loading) {
    return <LoadingSpinner fullScreen text="Loading campaign..." />;
  }

  if (!campaign) {
    return null;
  }

  const canExecute = !campaign.is_active && campaign.total_pending > 0;
  const hasFailedMessages = campaign.total_failed > 0;
  const canGenerateReceipt = campaign.status !== CampaignStatus.DRAFT && campaign.total_contacts > 0;
  const billedMessages = campaign.total_sent || campaign.total_contacts || 0;
  const amountDue = (
    billedMessages * (campaign.amount_per_sms || 0)
  ) + (campaign.workmanship_fee || 0) + (campaign.sender_id_price || 0);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/campaigns')}
            className="flex items-center text-blue-600 hover:text-blue-700 mb-4"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Campaigns
          </button>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{campaign.name}</h1>
              <p className="text-gray-600 mt-1">{campaign.description}</p>
            </div>
            <div className="flex gap-3">
              {canExecute && (
                <Button
                  variant="success"
                  onClick={handleExecuteCampaign}
                  loading={executing}
                >
                  {campaign.status === CampaignStatus.DRAFT ? 'Execute Campaign' : 'Send Pending Contacts'}
                </Button>
              )}
              {hasFailedMessages && (
                <Button variant="outline" onClick={handleRetryFailed}>
                  Retry Failed
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <p className="text-sm font-medium text-gray-600">Total Contacts</p>
            <p className="text-3xl font-bold text-gray-900 mt-2">
              {formatNumber(campaign.total_contacts)}
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <p className="text-sm font-medium text-gray-600">Sent</p>
            <p className="text-3xl font-bold text-blue-600 mt-2">
              {formatNumber(campaign.total_sent)}
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <p className="text-sm font-medium text-gray-600">Delivered</p>
            <p className="text-3xl font-bold text-green-600 mt-2">
              {formatNumber(campaign.total_delivered)}
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <p className="text-sm font-medium text-gray-600">Success Rate</p>
            <p className="text-3xl font-bold text-purple-600 mt-2">
              {formatPercentage(campaign.success_rate)}
            </p>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Campaign Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Message Template */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Message Template
              </h2>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-800 whitespace-pre-wrap">
                  {campaign.message_template}
                </p>
              </div>
              <div className="mt-3 text-sm text-gray-600">
                <span className="font-medium">Sender ID:</span> {campaign.sender_id}
              </div>
            </div>

            {/* Contacts Upload */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Contacts</h2>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/campaigns/${campaign.id}/contacts`)}
                  >
                    View Contacts
                  </Button>
                  <Button
                    variant="success"
                    size="sm"
                    onClick={() => setAddContactModalOpen(true)}
                  >
                    Add Contact
                  </Button>
                  {!campaign.is_active && (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => setUploadModalOpen(true)}
                    >
                      Upload Contacts
                    </Button>
                  )}
                </div>
              </div>

              {campaign.total_contacts === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500 mb-4">No contacts uploaded yet</p>
                  <div className="flex flex-col justify-center gap-3 sm:flex-row">
                    <Button onClick={() => setUploadModalOpen(true)}>
                      Upload Excel/CSV File
                    </Button>
                    <Button variant="outline" onClick={() => setAddContactModalOpen(true)}>
                      Add Contact Manually
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-gray-600">Total Contacts</span>
                    <span className="font-semibold">{formatNumber(campaign.total_contacts)}</span>
                  </div>
                  {campaign.upload_filename && (
                    <div className="flex justify-between py-2">
                      <span className="text-gray-600">Upload File</span>
                      <span className="text-sm text-gray-800">{campaign.upload_filename}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {canGenerateReceipt && <CampaignReceipt campaign={campaign} />}
          </div>

          {/* Right Column - Status & Timeline */}
          <div className="space-y-6">
            {/* Client Card */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Client Billing</h2>
              <div className="space-y-3 text-sm">
                <div>
                  <span className="text-gray-600">Client</span>
                  <p className="font-medium mt-1">{campaign.client_name || 'Not provided'}</p>
                </div>
                <div>
                  <span className="text-gray-600">Email</span>
                  <p className="font-medium mt-1 break-all">{campaign.client_email || 'Not provided'}</p>
                </div>
                <div className="pt-3 border-t">
                  <span className="text-gray-600">Amount Due</span>
                  <p className="text-xl font-bold text-gray-900 mt-1">
                    {campaign.currency || 'GHS'} {formatNumber(amountDue)}
                  </p>
                </div>
              </div>
            </div>

            {/* Status Card */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Status</h2>
              <div className="space-y-3">
                <div>
                  <span className="text-sm text-gray-600">Current Status</span>
                  <p className="text-lg font-semibold text-gray-900 mt-1">
                    {campaign.status.replace('_', ' ').toUpperCase()}
                  </p>
                </div>

                <div className="pt-3 border-t">
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Pending</span>
                      <span className="font-medium">{formatNumber(campaign.total_pending)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Failed</span>
                      <span className="font-medium text-red-600">{formatNumber(campaign.total_failed)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Timeline Card */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Timeline</h2>
              <div className="space-y-3 text-sm">
                <div>
                  <span className="text-gray-600">Created</span>
                  <p className="font-medium mt-1">{formatDate(campaign.created_at)}</p>
                </div>

                {campaign.started_at && (
                  <div>
                    <span className="text-gray-600">Started</span>
                    <p className="font-medium mt-1">{formatDate(campaign.started_at)}</p>
                  </div>
                )}

                {campaign.completed_at && (
                  <div>
                    <span className="text-gray-600">Completed</span>
                    <p className="font-medium mt-1">{formatDate(campaign.completed_at)}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Upload Modal */}
      <Modal
        isOpen={uploadModalOpen}
        onClose={() => {
          setUploadModalOpen(false);
          setSelectedFile(null);
        }}
        title="Upload Contacts"
        size="md"
        footer={
          <div className="flex gap-3 justify-end">
            <Button
              variant="outline"
              onClick={() => {
                setUploadModalOpen(false);
                setSelectedFile(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleFileUpload}
              disabled={!selectedFile}
              loading={uploading}
            >
              Upload
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Excel or CSV File
            </label>
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileSelect}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
          </div>

          {selectedFile && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <p className="text-sm text-blue-800">
                <span className="font-medium">Selected:</span> {selectedFile.name}
              </p>
            </div>
          )}

          <div className="bg-gray-50 rounded-md p-4 text-sm text-gray-600">
            <p className="font-medium mb-2">Required columns:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>name - Contact name</li>
              <li>phone_number - Phone number (with country code)</li>
            </ul>
            <p className="mt-2">Optional: email, custom_fields</p>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={addContactModalOpen}
        onClose={() => setAddContactModalOpen(false)}
        title="Add New Contact"
        size="lg"
      >
        <AddContactForm
          campaignId={campaign.id}
          onSuccess={async () => {
            setAddContactModalOpen(false);
            await fetchCampaignDetails(campaign.id);
          }}
          onCancel={() => setAddContactModalOpen(false)}
        />
      </Modal>
    </div>
  );
};

export default CampaignDetailsPage;
